import type { User } from '@/lib/auth-types';
import { ONBOARDING_BYPASS_PATHS } from '@/components/auth/OnboardingGuard';

/**
 * `/book/:bookOrderId/review` — reached from the book-arrived follow-up
 * mail. Dynamic path (bookOrderId is a UUID) so it cannot live in the
 * exact-match `ONBOARDING_BYPASS_PATHS` set the way `/einloesen` and
 * `/figuren` do. Matched with a tight pattern (UUID chars only, no
 * trailing slash, no further segments) so this honoring stays limited
 * to exactly the review deep link and never widens to arbitrary
 * `/book/.../something` paths. Route itself sits OUTSIDE
 * OnboardingGuard in App.tsx (same tree as /book/:orderId), so a
 * logged-in book-only user visiting the URL already renders directly;
 * this helper only matters for the post-login returnTo flow.
 */
const BOOK_REVIEW_RETURN_TO = /^\/book\/[0-9a-fA-F-]{36}\/review$/;

export function isBookOnlyReturnTo(returnTo: string | null | undefined): boolean {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('/auth/')) {
    return false;
  }
  const pathname = returnTo.split(/[?#]/, 1)[0] ?? '';
  return ONBOARDING_BYPASS_PATHS.has(pathname) || BOOK_REVIEW_RETURN_TO.test(pathname);
}

/**
 * Where the customer goes after a successful login / signup /
 * email-verification.
 *
 * ═══ EXECUTION ORDER (Sebastian ruling 2026-08-06) ═══════════════════
 *
 *   1. `user.pendingBookOrderId` → `/book/${id}`
 *      The customer left a pre-payment book order behind and is
 *      coming back. Land him on his half-built book, not on
 *      `/onboarding/welcome`. Backend computes this over the
 *      pre-payment status list at Bau-Spez v3.1 §7.
 *
 *   2. `user.signupSource === 'book_lightgate'` → `/book`
 *      The customer registered through the Light-Gate but has no
 *      pending order (order created + finished + moved past
 *      pre-payment, OR the order failed and there's nothing to
 *      resume). Land him on the Konfigurator, not on the audio
 *      onboarding.
 *
 *   3. Existing audio logic, untouched:
 *      3a. `!user.emailVerified` → `/auth/confirm?email=...`
 *      3b. `!user.onboardingCompleted` → `/onboarding/welcome`
 *      3c. `returnTo` → that path (audio-side login-return flow)
 *      3d. else → `/`
 *
 * ═══ WHAT THIS MUST NOT BREAK (Sebastian ruling) ═════════════════════
 *
 * `onboardingCompleted` STAYS an audio precondition. It gates audio
 * routes and never book routes. An audio customer with no child
 * profile still lands in the audio onboarding exactly as today —
 * covered by the "audio customer route unchanged" test in
 * `post-auth-redirect.test.ts`. Book routes sit under different auth
 * branches per the P5-5 route-fix in `App.tsx` (2026-08-06 Sebastian
 * ruling): `/book` sits OUTSIDE `ProtectedRoute` entirely — a
 * visitor without a session can reach the configurator per §A4
 * "Konfigurieren komplett offen und kostenlos"; `/book/:orderId`
 * sits INSIDE a nested `ProtectedRoute` because an existing order
 * belongs to an account. Neither leaf is behind `OnboardingGuard`,
 * so a book customer with `onboardingCompleted === false` (which
 * every book customer has, because they never see the audio
 * child-profile screen) reaches the book flow without being
 * intercepted.
 *
 * ═══ EMAIL VERIFICATION ══════════════════════════════════════════════
 *
 * The `!emailVerified → /auth/confirm` bypass at line 3a fires ONLY
 * on Cases 3+. For Cases 1 and 2 (book customer), verification stays
 * required but not between the customer and his title image — the
 * gate lives at `src/checkout/checkout.controller.ts:22` (Phase-6
 * Bestellung), and the `BookShell.verifyReminderLine` (5a Commit 4)
 * carries the resend action in the meantime. See tracker
 * `[book-light-gate-email-verification-blocks-flow]`.
 */
export function computePostAuthRedirect(user: User, returnTo?: string | null): string {
  // Probecover Stage 1 (Sebastian 2026-10-15) — an explicit
  // returnTo=/figuren wins over the pendingBookOrderId Case 1
  // below. Rationale: the lead-magnet entry URL has to beat the W28
  // pending-draft redirect so a customer who clicks a /figuren link
  // (e.g. Stage 3's Webflow landing page) lands on /figuren even if
  // he already has an open book draft. The W28 redirect WHEN
  // ENTERING /book itself stays untouched — this check only fires
  // on an explicit auth-return path, exactly like the
  // ONBOARDING_BYPASS_PATHS returnTo honoring at the bottom of this
  // function.
  if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('/auth/')) {
    const pathname = returnTo.split(/[?#]/, 1)[0] ?? '';
    if (pathname === '/figuren') {
      return returnTo;
    }
  }

  // Book-arrived review deep link (feat/book-review-followup, Sebastian
  // 2026-10-21) — honor an explicit returnTo=/book/<uuid>/review BEFORE
  // Case 1 (pendingBookOrderId) and Case 2 (book_lightgate default
  // landing). Rationale: a book-only buyer following the follow-up mail
  // was previously redirected to /onboarding/welcome because the
  // existing `ONBOARDING_BYPASS_PATHS` honoring at the bottom of this
  // function is an exact-match set and the dynamic review URL never
  // matched. Mirrors the /figuren precedent above. Matches only the
  // tight review-URL shape via `isBookOnlyReturnTo`; cannot widen to
  // arbitrary /book/... paths.
  if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('/auth/')) {
    const pathname = returnTo.split(/[?#]/, 1)[0] ?? '';
    if (BOOK_REVIEW_RETURN_TO.test(pathname)) {
      return returnTo;
    }
  }

  // Case 1 — has a pre-payment book order in flight. Land there.
  if (user.pendingBookOrderId !== null) {
    return `/book/${user.pendingBookOrderId}`;
  }
  // Case 2 — book customer without a pending order. Land in the
  // Konfigurator, not in audio onboarding.
  if (user.signupSource === 'book_lightgate') {
    return '/book';
  }
  // Case 2b (F3, Sebastian field finding 2026-10-16) — probe
  // customer. Mirrors Case 2 for book_lightgate: never into audio
  // onboarding, never through /auth/confirm first. Target:
  //   - any book order on the account → /book/meine (returning
  //     customer with a book collection).
  //   - otherwise → /figuren (either land on an existing probe
  //     via the deep-link mail, or fill a fresh form).
  // `pendingBookOrderId` Case 1 above keeps its priority — a
  // probe customer with an unfinished book draft still resumes
  // the book first. An explicit returnTo=/figuren at the top of
  // this function also keeps winning (mail deep links).
  if (user.signupSource === 'probecover') {
    return user.hasBookWorld ? '/book/meine' : '/figuren';
  }
  // Case 3 — existing audio logic, unchanged.
  if (!user.emailVerified) {
    return `/auth/confirm?email=${encodeURIComponent(user.email)}`;
  }
  // Sebastian ruling 2026-09-25 — a returnTo whose pathname is in
  // ONBOARDING_BYPASS_PATHS is honored BEFORE the audio-onboarding
  // gate. A fresh gift recipient signing up via the /einloesen link
  // lands on the redeem page, not on /onboarding/welcome. Same
  // OnboardingGuard bypass set is the single source of truth.
  //
  // `isBookOnlyReturnTo` covers both the static set AND the dynamic
  // `/book/<uuid>/review` deep link so a book-only user who signs up
  // via the follow-up mail reaches the review page even when the
  // top-of-function early-return at Case 1/2 priority did not catch
  // them (e.g. an audio-flavoured account with onboarding incomplete
  // that happened to click a book review link).
  if (isBookOnlyReturnTo(returnTo)) {
    return returnTo!;
  }
  if (!user.onboardingCompleted) {
    return '/onboarding/welcome';
  }
  if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('/auth/')) {
    return returnTo;
  }
  return '/';
}
